import React, {useEffect} from "react";
import {useRouteError} from "react-router-dom";
import {FFButton} from "ui/FFButton";
import {tagClarity} from "clarity";

function isChunkLoadError(error: unknown): boolean {
    if (error instanceof Error) {
        return error.name === 'ChunkLoadError'
            || error.message.includes('Loading chunk')
            || error.message.includes('Loading CSS chunk');
    }
    return false;
}

const WifiOffIcon = () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
        <path d="M5 12.55a10.94 10.94 0 0 1 14 0"/>
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
        <circle cx="12" cy="20" r="0.5" fill="currentColor"/>
        <line x1="3" y1="3" x2="21" y2="21"/>
    </svg>
);

const WarningIcon = () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

export const ErrorPage = () => {
    const error = useRouteError();
    const isNetwork = isChunkLoadError(error);

    // Nothing else records what took the page down: the router catches it before any
    // global handler, so the Clarity recording is the only place it can be read later.
    useEffect(() => {
        const text = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
        tagClarity('error_page', text.slice(0, 200));
    }, [error]);

    const title = isNetwork
        ? "Connection lost"
        : "Something went wrong";

    const description = isNetwork
        ? "Failed to load the page due to a network issue. Please check your internet connection and try again."
        : "An unexpected error occurred. Please reload the page.";

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '24px',
            boxSizing: 'border-box',
            textAlign: 'center',
        }}>
            <div style={{color: 'var(--color-secondary)', marginBottom: '24px'}}>
                {isNetwork ? <WifiOffIcon/> : <WarningIcon/>}
            </div>
            <h1 style={{
                fontSize: '24px',
                fontWeight: 700,
                margin: '0 0 12px 0',
                color: 'var(--color-primary)',
            }}>
                {title}
            </h1>
            <p style={{
                fontSize: '16px',
                color: 'var(--color-secondary)',
                margin: '0 0 32px 0',
                maxWidth: '400px',
                lineHeight: '1.5',
            }}>
                {description}
            </p>
            <FFButton onClick={() => window.location.reload()}>
                Reload page
            </FFButton>
        </div>
    );
};
